import { T, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { CheckCircle2 } from 'lucide-react'

import {
  extractPizzaSize,
  giftNameOf,
  isGarlicCrust,
  isGiftItem,
  mergeIngredientsAndExtras,
  ingredientChipClasses,
  ingredientCountClasses,
  parseItemName,
  type MergedIngredient,
} from './item-name'
import type { ItemProgress } from './elapsed'
import { formatAmount, type RecipeIngredient } from './recipes'
import type { KitchenLine } from './kitchen-items'

/**
 * One line on a kitchen card, and the thing a cook actually taps.
 *
 * Tapping cycles it through untouched → being made → made. That state lives on
 * the device and is never sent anywhere: it is a cook's scratch mark on their
 * own screen, and two tablets keep their own (ported behaviour).
 */

/** Product id → the kitchen-only description the menu editor wrote. */
export interface KitchenDescriptions {
  byId: Record<number, string>
  byName: Record<string, string>
  /** Product id → whether its description may be shown at all. */
  showInKitchen: Record<number, boolean>
  /**
   * Product id → its recipe's ingredients — `FRONTEND.md` §9.55, the owner's *"malo by byť vidieť
   * u každého produktu to, čo je nastavené ako RECEPT"*.
   *
   * **Kept beside the prose description rather than replacing it, and the data is why.** Measured on
   * the restored menu: 87 products have a recipe, 95 have a description, and the two overlap on only
   * 44. Preferring the recipe alone would blank 51 products that have only prose; keeping only the
   * prose leaves 43 showing a bare name, which is what the kitchen does today. Together they cover
   * 138 of 173.
   */
  recipes: Record<number, RecipeIngredient[]>
  /** Lower-cased product name → the same, for a kombo half that is only a name. */
  recipesByName: Record<string, RecipeIngredient[]>
}

/** Strips whatever size badge was pulled out, so the name is not repeated. */
const SIZE_IN_NAME =
  /^\d{2,3}\s?cm\s*|\s*[-–—]+\s*\d{2,3}\s?cm|\s*[([]\s*\d{2,3}\s?cm\s*[)\]]|\s+\d{2,3}\s?cm/gi

function baseProductId(productId: number): number {
  return Math.floor(productId / 1000) || productId
}

export function KitchenItemCard({
  line,
  descriptions,
  progress,
  onToggle,
}: {
  line: KitchenLine
  descriptions: KitchenDescriptions
  progress: ItemProgress
  onToggle: () => void
}) {
  const productId = line.productId ?? null
  const realId = productId ? baseProductId(productId) : null

  const mayShowDescription =
    !productId ||
    (descriptions.showInKitchen[productId] !== false &&
      descriptions.showInKitchen[realId ?? productId] !== false)

  const kitchenDescription =
    mayShowDescription && productId
      ? (descriptions.byId[productId] ?? descriptions.byId[Math.floor(productId / 1000)] ?? null)
      : null

  // The recipe is preferred where there is one: where both exist they say the same thing, and the
  // recipe is the one carrying weights — *Trhané hovädzie 150 g* rather than *trhané hovädzie*.
  const recipe =
    mayShowDescription && productId
      ? (descriptions.recipes[productId] ?? descriptions.recipes[baseProductId(productId)] ?? null)
      : null

  const gift = isGiftItem(line.productName)
  const rawName = gift ? giftNameOf(line.productName) : (line.productName ?? '')
  const pizzaSize = extractPizzaSize(rawName)
  const parsed = parseItemName(pizzaSize ? rawName.replace(SIZE_IN_NAME, '').trim() : rawName)

  const isDone = progress === 'done'
  const isWorking = progress === 'working'

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'w-full text-left rounded-lg border transition-all duration-200 px-2.5 py-2 flex items-start gap-2',
        isDone
          ? 'bg-emerald-500/10 border-emerald-500/25 opacity-50'
          : isWorking
            ? 'bg-yellow-500/10 border-yellow-500/30 ring-1 ring-yellow-500/40'
            : 'bg-white/3 border-white/8 hover:bg-white/6',
        gift && !isWorking && !isDone && 'bg-amber-500/8 border-amber-500/15',
      )}
    >
      <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
        <span
          className={cn(
            'min-w-[28px] h-[28px] rounded text-[13px] font-black flex items-center justify-center leading-none',
            isDone
              ? 'bg-emerald-500/25 text-emerald-400'
              : isWorking
                ? 'bg-yellow-500/25 text-yellow-300'
                : gift
                  ? 'bg-amber-500/25 text-amber-400'
                  : (line.quantity ?? 1) >= 2
                    ? 'bg-orange-500/30 text-orange-300'
                    : 'bg-white/10 text-white/90',
          )}
        >
          {line.quantity}×
        </span>
        {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
        {isWorking && <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />}
      </div>

      <div className="flex flex-col min-w-0 gap-0.5 flex-1">
        {gift && (
          <span className="text-[9px] font-bold text-amber-400 uppercase tracking-wider">
            <T>🎁 Darček</T>
          </span>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={cn(
              'text-[13px] font-black leading-tight',
              isDone ? 'text-white/40 line-through' : 'text-white',
            )}
          >
            <T>{parsed.name}</T>
          </span>
          {pizzaSize && (
            <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-orange-500/25 text-orange-300 leading-none flex-shrink-0">
              {pizzaSize}
            </span>
          )}
        </div>

        {parsed.isKombo && parsed.description ? (
          <KomboHalves halves={parsed.description} descriptions={descriptions} />
        ) : recipe && recipe.length > 0 ? (
          <RecipeList ingredients={recipe} extras={parsed.extras} />
        ) : (
          mayShowDescription && (
            <SingleIngredients
              description={kitchenDescription ?? parsed.description}
              extras={parsed.extras}
            />
          )
        )}
      </div>
    </button>
  )
}

/**
 * A kombo is two pizzas in one box, and the cook needs both recipes. Legacy
 * looks each half up by name, because the halves are only names in the line.
 */
function KomboHalves({
  halves,
  descriptions,
}: {
  halves: string
  descriptions: KitchenDescriptions
}) {
  return (
    <div className="flex flex-col gap-0.5 mt-0.5">
      {halves.split(' · ').map((half, position) => {
        const name = half.trim()
        // Same preference as a single line: the recipe where there is one, the prose otherwise.
        const halfRecipe = descriptions.recipesByName[name.toLowerCase()] ?? null
        const prose = descriptions.byName[name.toLowerCase()] ?? null
        const merged = prose ? mergeIngredientsAndExtras(prose, null) : []
        return (
          // §9.406 - **keyed by position, not by name.** Measured on the owner's data: all 140 of
          // their kombo lines carry three pizzas and **24 repeat one**, so `key={name}` was a
          // duplicate key on one card in six - which React resolves by reusing an element, so a
          // change to one half can paint on the other.
          <div
            key={position}
            className="flex flex-col gap-0.5 pl-1 border-l-2 border-orange-500/30"
            data-kombo-half={position + 1}
          >
            <span className="text-[11px] font-black text-white/80 leading-tight">
              {/* §9.406, the owner's ask: *Pizza 1 / Pizza 2 / Pizza 3*. A number rather than the
                * word, because the header above already says *Kombo Pizza* and a card is read
                * across a kitchen. On the 24 lines with a repeated pizza it is the only thing that
                * says two identical names are two pizzas. */}
              <span className="text-orange-400/80">{position + 1}.</span> {name}
            </span>
            {halfRecipe && halfRecipe.length > 0 ? (
              <RecipeList ingredients={halfRecipe} extras={null} />
            ) : (
              merged.length > 0 && <IngredientsList merged={merged} />
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * A recipe, as weights rather than prose — §9.55.
 *
 * Anything the customer added on top still comes from the line's own `[extras]`, because an extra is
 * not part of the recipe by definition: it is what this customer asked for beyond it.
 */
function RecipeList({
  ingredients,
  extras,
}: {
  ingredients: RecipeIngredient[]
  extras: string | null
}) {
  return (
    <div className="flex flex-col gap-0.5 mt-0.5">
      <div className="flex flex-col gap-0.5">
        {ingredients.map((ingredient, index) => (
          <span
            // Ingredient names repeat within a recipe; the index is what keeps two rows distinct.
            key={`${ingredient.name}-${index}`}
            className={cn(
              'inline-flex items-center gap-1 text-[10px] rounded px-1 py-0.5 leading-tight',
              ingredientChipClasses({}),
            )}
          >
            {formatAmount(ingredient) !== null && (
              <span className={ingredientCountClasses({})}>{formatAmount(ingredient)}</span>
            )}
            <T>{ingredient.name}</T>
          </span>
        ))}
      </div>
      {extras && <SingleIngredients description={null} extras={extras} />}
    </div>
  )
}

function SingleIngredients({
  description,
  extras,
}: {
  description: string | null
  extras: string | null
}) {
  if (!description && !extras) return null
  return <IngredientsList merged={mergeIngredientsAndExtras(description, extras)} />
}

function IngredientsList({ merged }: { merged: MergedIngredient[] }) {
  return (
    <div className="flex flex-col gap-0.5 mt-0.5">
      {merged.map((ingredient, index) => {
        const garlic = isGarlicCrust(ingredient.name)
        return (
          <span
            // Ingredient names repeat across an order; the index is what makes
            // two chips reading the same thing distinct.
            key={`${ingredient.name}-${index}`}
            className={cn(
              'inline-flex items-center gap-0.5 text-[10px] rounded px-1 py-0.5 leading-tight',
              ingredientChipClasses({ garlic, standalone: ingredient.standalone }),
            )}
          >
            {garlic && <span aria-label={tt('cesnakové kraje')}>🧄</span>}
            <span className={ingredientCountClasses({ garlic, boosted: ingredient.boosted })}>
              {ingredient.count}×
            </span>
            <T>{ingredient.name}</T>
          </span>
        )
      })}
    </div>
  )
}
